import { useState, type FormEvent } from "react"
import { IconChevronDown, IconLoader2, IconSearch } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { FieldError } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { statusLabel } from "@/app/booking-calendar/lib/bookingStatus"
import { HostPicker } from "./HostPicker"
import {
  MAX_RANGE_DAYS,
  SEARCHABLE_STATUSES,
  SSN_LENGTH,
  TEXT_CRITERIA,
  formatSsnInput,
  statusSummary,
  toggleStatus,
  type SearchForm,
  type SearchFormValidation,
} from "../searchCriteria"

export interface SearchCriteriaFormProps {
  form: SearchForm
  validation: SearchFormValidation
  /** A search is on its way; the button says so. */
  searching?: boolean
  onChange: (form: SearchForm) => void
  onSubmit: () => void
  onReset: () => void
}

const RANGE_ERROR_ID = "search-range-error"

/**
 * The criteria. Search stays off until the form is one the server would accept, and every field that
 * stops it says why beneath itself.
 */
export function SearchCriteriaForm({ form, validation, searching = false, onChange, onSubmit, onReset }: SearchCriteriaFormProps) {
  const { errors } = validation
  // A text field's message waits until the field is left, so a phone number is not called wrong
  // while it is still being typed.
  const [left, setLeft] = useState<ReadonlySet<string>>(() => new Set())
  const set = <K extends keyof SearchForm>(key: K, value: SearchForm[K]) => onChange({ ...form, [key]: value })

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (validation.valid) onSubmit()
  }

  const rangeInvalid = !!errors.range
  const markLeft = (key: string) => setLeft(current => (current.has(key) ? current : new Set([...current, key])))
  const ssnError = left.has("ssn") ? errors.ssn : undefined

  return (
    <form className="flex flex-col gap-4 rounded-xl border bg-card p-4" onSubmit={submit} aria-label="Search appointments" noValidate>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* First, right before Customer name (AB#817). Offered to every appointments user (AB#845); the
            server audits each search by it. Shown in clear as it is typed, formatted xxx-xx-xxxx (the
            human's decision 2026-10-30: staff are at their own screens, and the page clears it as it
            searches). Still kept out of autofill and password managers: it is a lookup key, not a
            credential, and must not be offered for saving. maxLength holds the nine digits and two dashes. */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="search-ssn" className="text-xs">Social Security number</Label>
          <Input
            id="search-ssn"
            name="ssn-lookup"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            maxLength={SSN_LENGTH + 2}
            value={form.ssn}
            onChange={event => set("ssn", formatSsnInput(event.target.value))}
            onBlur={() => markLeft("ssn")}
            aria-invalid={ssnError ? true : undefined}
            aria-describedby={[ssnError ? "search-ssn-error" : undefined, "search-ssn-hint"].filter(Boolean).join(" ")}
            data-1p-ignore=""
            data-lpignore="true"
            data-bwignore=""
            data-form-type="other"
          />
          <p id="search-ssn-hint" className="text-xs text-muted-foreground">All nine digits.</p>
          {ssnError && <FieldError id="search-ssn-error">{ssnError}</FieldError>}
        </div>
        {TEXT_CRITERIA.map(criterion => {
          const id = `search-${criterion.key}`
          const error = left.has(criterion.key) ? errors[criterion.key] : undefined
          const describedBy = [error ? `${id}-error` : undefined, criterion.hint ? `${id}-hint` : undefined].filter(Boolean).join(" ")
          return (
            <div key={criterion.key} className="flex flex-col gap-1.5">
              <Label htmlFor={id} className="text-xs">{criterion.label}</Label>
              <Input
                id={id}
                name={criterion.key}
                type={criterion.inputType}
                autoComplete={criterion.autoComplete}
                maxLength={criterion.maxLength}
                placeholder={criterion.placeholder}
                value={form[criterion.key]}
                onChange={event => set(criterion.key, event.target.value)}
                inputMode={criterion.inputMode}
                onBlur={() => markLeft(criterion.key)}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy || undefined}
              />
              {criterion.hint && <p id={`${id}-hint`} className="text-xs text-muted-foreground">{criterion.hint}</p>}
              {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
            </div>
          )
        })}
      </div>

      <div className="flex flex-wrap items-start gap-4">
        {/* AB#844: a host is a criterion of its own, with the range; a site narrows it. Right before
            Status (AB#817). */}
        <HostPicker
          siteSlug={form.siteSlug}
          hostCalUserId={form.hostCalUserId}
          error={errors.hostCalUserId}
          onChange={next => onChange({ ...form, ...next })}
        />

        <div className="flex flex-col gap-1.5">
          <span id="search-status-label" className="text-xs font-medium">Status</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-56 justify-between font-normal" aria-labelledby="search-status-label search-status-value">
                <span id="search-status-value" className="truncate">{statusSummary(form.statuses)}</span>
                <IconChevronDown className="size-4 opacity-60" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {SEARCHABLE_STATUSES.map(status => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={form.statuses.includes(status)}
                  // Stays open, so several statuses can be ticked in one go.
                  onSelect={event => event.preventDefault()}
                  onCheckedChange={checked => set("statuses", toggleStatus(form.statuses, status, checked === true))}
                >
                  {statusLabel(status)}
                </DropdownMenuCheckboxItem>
              ))}
              {form.statuses.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => set("statuses", [])}>Any status</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <fieldset className="flex flex-col gap-1.5" aria-describedby={rangeInvalid ? RANGE_ERROR_ID : undefined}>
          <legend className="sr-only">Appointment dates (Pacific)</legend>
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="search-from" className="text-xs">From (Pacific)</Label>
              <Input
                id="search-from"
                type="date"
                className="w-40"
                required
                value={form.from}
                max={form.to || undefined}
                onChange={event => set("from", event.target.value)}
                aria-invalid={errors.from || rangeInvalid ? true : undefined}
                aria-describedby={errors.from ? "search-from-error" : undefined}
              />
              {errors.from && <FieldError id="search-from-error">{errors.from}</FieldError>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="search-to" className="text-xs">To (Pacific)</Label>
              <Input
                id="search-to"
                type="date"
                className="w-40"
                required
                value={form.to}
                min={form.from || undefined}
                onChange={event => set("to", event.target.value)}
                aria-invalid={errors.to || rangeInvalid ? true : undefined}
                aria-describedby={errors.to ? "search-to-error" : undefined}
              />
              {errors.to && <FieldError id="search-to-error">{errors.to}</FieldError>}
            </div>
          </div>
          {errors.range
            ? <FieldError id={RANGE_ERROR_ID}>{errors.range}</FieldError>
            : <p className="text-xs text-muted-foreground">Both days included, up to {MAX_RANGE_DAYS} days. On its own, lists every appointment in it.</p>}
        </fieldset>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={!validation.valid || searching}>
          {searching ? <IconLoader2 className="size-4 animate-spin" aria-hidden="true" /> : <IconSearch className="size-4" aria-hidden="true" />}
          Search
        </Button>
        <Button type="button" variant="ghost" onClick={onReset}>Clear</Button>
      </div>
    </form>
  )
}
